import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate } from "react-router-dom";

import Quiz from "./components/Quiz";
import Header from "./components/Header";
import ProgressBar from "./components/ProgressBar";
import AccessibilityStatement from "./components/AccessibilityStatement";

import "./index.css";
import { useTranslation } from "react-i18next";

const SUPPORTED_LANGS = ["he", "ar"];

function normalizeLang(lng) {
    const normalized = (lng || "he").toLowerCase();
    return SUPPORTED_LANGS.includes(normalized) ? normalized : "he";
}

function useSyncLangToHtml(lang) {
    const { i18n } = useTranslation();

    useEffect(() => {
        if (i18n.language !== lang) i18n.changeLanguage(lang);

        document.documentElement.lang = lang;
        document.documentElement.dir = "rtl";
    }, [i18n, lang]);
}

function BackToQuizButton() {
    const navigate = useNavigate();
    const { lng } = useParams();
    const lang = normalizeLang(lng);
    const { t } = useTranslation();

    return (
        <div className="text-center mt-6">
            <button
                onClick={() => navigate(`/${lang}`)}
                className="cursor-pointer mt-4 px-6 py-2 bg-white border border-gray-400 rounded text-darktext font-medium hover:bg-gray-100 transition"
            >
                {t("common.backToQuiz")}
            </button>
        </div>
    );
}

function QuizPage({ progress, setProgress }) {
    const { lng } = useParams();
    const lang = normalizeLang(lng);
    const { t } = useTranslation();

    useSyncLangToHtml(lang);

    return (
           <>
            <main className="relative min-h-screen bg-cream text-darktext text-right space-y-6 sm:space-y-10 md:space-y-12 overflow-x-clip" dir="rtl">
            <Header>
                <div className="w-full">
                    <ProgressBar progress={progress}/>
                </div>
            </Header>

            <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 md:px-8">
                <Quiz onProgressChange={setProgress}/>

                <div className="text-center mt-6">
                    <a
                        href={`/${lang}/accessibility`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-lime-700 hover:text-lime-800 text-sm sm:text-base"
                        aria-label={t("common.accessibility")}
                    >
                        {t("common.accessibility")}
                    </a>
                </div>
            </div>
        </main>
           </>
    );
}

function AccessibilityPage() {
    const {lng} = useParams();
    const lang = normalizeLang(lng);

    useSyncLangToHtml(lang);

    return (
        <main className="min-h-screen bg-cream text-darktext text-right p-6" dir="rtl" role="main">
            <Header />

            <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 md:px-8 space-y-6">
                <AccessibilityStatement />
                <BackToQuizButton />
            </div>
        </main>
    );
}

export default function App() {
    const [progress, setProgress] = useState(0);

    return (
        <BrowserRouter>
            <Routes>
                <Route path="/" element={<Navigate to="/he" replace />} />
                <Route path="/:lng" element={<QuizPage progress={progress} setProgress={setProgress} />} />
                <Route path="/:lng/accessibility" element={<AccessibilityPage />} />
                <Route path="*" element={<Navigate to="/he" replace />} />
            </Routes>
        </BrowserRouter>
    );
}